import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Form, Row, Col, Badge, Modal,
  Spinner, OverlayTrigger, Tooltip, InputGroup, Pagination, ProgressBar
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const DataPURS = () => {
  const navigate = useNavigate();

  // Helper IP API
  const getApiUrl = () => {
    return localStorage.getItem('api_url') || 'http://localhost:8000';
  };

  // Helper Tanggal YYYY-MM-DD (untuk input picker) & DD/MM/YYYY (untuk parameter backend)
  const getTodayYmd = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatYmdToDdmmyyyy = (ymdStr) => {
    if (!ymdStr) return '';
    if (ymdStr.includes('/')) return ymdStr;
    const parts = ymdStr.split('-');
    if (parts.length === 3) {
      const [yyyy, mm, dd] = parts;
      return `${dd}/${mm}/${yyyy}`;
    }
    return ymdStr;
  };

  // Helper Format Rupiah
  const formatRp = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);
  };

  // State Date Range Filter (periode & periode2 default hari ini)
  const [periode, setPeriode]   = useState(getTodayYmd());
  const [periode2, setPeriode2] = useState(getTodayYmd());

  // State Active Tab: 'purs' | 'ppurs'
  const [activeTab, setActiveTab] = useState('purs');

  // State Data PURS (/show_purs) & PPURS (/show_ppurs)
  const [pursList, setPursList]     = useState([]);
  const [ppursList, setPpursList]   = useState([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State Clock
  const [time, setTime] = useState({ date: '', clock: '' });

  // State Filter & Pagination
  const [searchQuery, setSearchQuery]   = useState('');
  const [filterUrgensi, setFilterUrgensi] = useState(''); // '', 'Cito', 'Tidak Cito'
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // State Modal Detail PURS / PPURS
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPurs, setSelectedPurs]       = useState(null);
  const [selectedPpurs, setSelectedPpurs]     = useState(null);

  // State Modal Sinkronisasi Data Smartsheet (/getdatasmartsheet)
  const [showSyncModal, setShowSyncModal]   = useState(false);
  const [isSyncing, setIsSyncing]           = useState(false);
  const [syncProgress, setSyncProgress]     = useState(0);
  const [syncStatusText, setSyncStatusText] = useState('');
  const [syncFeedback, setSyncFeedback]     = useState(null);

  // Mock Initial Data PURS (/show_purs)
  const mockPursData = [
    {
      "kd_divisi": "004",
      "kd_unit": "012",
      "keterangan": "MAKAN OK TGL 3",
      "nik_kabag": "24.11.0601",
      "nik_pemohon": "01.09.1101",
      "nilaipengajuan": 454000,
      "nm_divisi": "Bagian Keuangan",
      "nm_kabag": "Nurul Amaliya",
      "nm_pemohon": "Rizki F",
      "nm_unit": "Kasir IGD",
      "no_anggaran": "OK001",
      "no_ppurs": "PPURS/12/2025/8",
      "no_purs": "PURS/12/2025/8",
      "no_urut": "3130",
      "saldoanggaran": 63376300,
      "tanggal": "04/12/2025",
      "urgensi": "Tidak Cito",
      "url_img1": null,
      "url_img2": null,
      "url_img3": null
    },
    {
      "kd_divisi": "001",
      "kd_unit": "003",
      "keterangan": "RUJUK SAMPEL SHK",
      "nik_kabag": "02.05.0604",
      "nik_pemohon": "20.12.2401",
      "nilaipengajuan": 80000,
      "nm_divisi": "Bidang Keperawatan dan Penunjang Klinis",
      "nm_kabag": "Roni Kurniawan",
      "nm_pemohon": "RAHMAIDA APRIYANI",
      "nm_unit": "Laboratorium",
      "no_anggaran": "LAB 001",
      "no_ppurs": "PPURS/12/2025/7",
      "no_purs": "PURS/12/2025/7",
      "no_urut": "3129",
      "saldoanggaran": 17500000,
      "tanggal": "03/12/2025",
      "urgensi": "Cito",
      "url_img1": null,
      "url_img2": null,
      "url_img3": null
    }
  ];

  // Mock Initial Data PPURS (/show_ppurs)
  const mockPpursData = [
    {
      "bank": 0,
      "dokumen": 454000,
      "kelompok": "Belanja Konsumsi Dokter dan Pegawai",
      "nilaipengajuan": 454000,
      "nm_pemohon": "Rizki F",
      "no_anggaran": "OK001",
      "no_ppurs": "PPURS/12/2025/8",
      "no_purs": "PURS/12/2025/8",
      "sisa_purs": 0,
      "tanggal": "04/12/2025",
      "tunai": 0
    },
    {
      "bank": 0,
      "dokumen": 80000,
      "kelompok": "Kirim Dokumen dan Paket",
      "nilaipengajuan": 80000,
      "nm_pemohon": "RAHMAIDA APRIYANI",
      "no_anggaran": "LAB 001",
      "no_ppurs": "PPURS/12/2025/7",
      "no_purs": "PURS/12/2025/7",
      "sisa_purs": 0,
      "tanggal": "03/12/2025",
      "tunai": 0
    }
  ];

  // ── Fetch Data PURS Backend show_purs ──
  const fetchPursData = async () => {
    setIsLoading(true);
    setFetchError(null);
    const apiUrl = getApiUrl();
    const formattedPeriode1 = formatYmdToDdmmyyyy(periode);
    const formattedPeriode2 = formatYmdToDdmmyyyy(periode2);

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/show_purs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            periode: formattedPeriode1,
            periode2: formattedPeriode2
          })
        });
      } catch {
        res = await fetch(`${apiUrl}/show_purs?periode=${encodeURIComponent(formattedPeriode1)}&periode2=${encodeURIComponent(formattedPeriode2)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      let dataArray = [];
      if (Array.isArray(json)) {
        dataArray = json;
      } else if (json?.list_purs && Array.isArray(json.list_purs)) {
        dataArray = json.list_purs;
      } else {
        dataArray = mockPursData;
      }

      setPursList(dataArray);
    } catch (err) {
      console.warn('Gagal fetch /show_purs:', err);
      setPursList(mockPursData);
      setFetchError('Menggunakan data lokal. Gagal terhubung ke backend server.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Fetch Data PPURS Backend show_ppurs ──
  const fetchPpursData = async () => {
    setIsLoading(true);
    setFetchError(null);
    const apiUrl = getApiUrl();
    const formattedPeriode1 = formatYmdToDdmmyyyy(periode);
    const formattedPeriode2 = formatYmdToDdmmyyyy(periode2);

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/show_ppurs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            periode: formattedPeriode1,
            periode2: formattedPeriode2
          })
        });
      } catch {
        res = await fetch(`${apiUrl}/show_ppurs?periode=${encodeURIComponent(formattedPeriode1)}&periode2=${encodeURIComponent(formattedPeriode2)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      let dataArray = [];
      if (Array.isArray(json)) {
        dataArray = json;
      } else if (json?.list_ppurs && Array.isArray(json.list_ppurs)) {
        dataArray = json.list_ppurs;
      } else {
        dataArray = mockPpursData;
      }

      setPpursList(dataArray);
    } catch (err) {
      console.warn('Gagal fetch /show_ppurs:', err);
      setPpursList(mockPpursData);
      setFetchError('Menggunakan data lokal. Gagal terhubung ke backend server.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Handler Sinkron Data Smartsheet (getdatasmartsheet) ──
  const handleOpenSyncModal = () => {
    setSyncProgress(0);
    setSyncStatusText('');
    setSyncFeedback(null);
    setIsSyncing(false);
    setShowSyncModal(true);
  };

  const handleCloseSyncModal = () => {
    if (isSyncing) return;
    setShowSyncModal(false);
    setSyncFeedback(null);
  };

  const handleProcessSyncSmartsheet = async () => {
    setIsSyncing(true);
    setSyncProgress(15);
    setSyncStatusText('Sedang Mengakses & Mengunduh Data dari Smartsheet...');
    setSyncFeedback(null);

    const apiUrl = getApiUrl();
    const formattedPeriode1 = formatYmdToDdmmyyyy(periode);
    const formattedPeriode2 = formatYmdToDdmmyyyy(periode2);

    const progressInterval = setInterval(() => {
      setSyncProgress(prev => (prev < 85 ? prev + 15 : prev));
    }, 200);

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/getdatasmartsheet`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            periode: formattedPeriode1,
            periode2: formattedPeriode2
          })
        });
      } catch {
        res = await fetch(`${apiUrl}/getdatasmartsheet?periode=${encodeURIComponent(formattedPeriode1)}&periode2=${encodeURIComponent(formattedPeriode2)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      clearInterval(progressInterval);
      setSyncProgress(100);

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      setSyncStatusText('Sinkronisasi Berhasil Selesai!');
      setSyncFeedback({
        type: 'success',
        text: json?.msg || json?.message || 'Data pengeluaran PURS dari Smartsheet berhasil disinkronkan ke SIMRS!'
      });

      // Refresh data PURS
      setTimeout(() => {
        fetchPursData();
      }, 1000);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal sync /getdatasmartsheet:', err);
      setSyncProgress(100);
      setSyncStatusText('Proses Sinkronisasi Selesai (Demo Mode).');
      setSyncFeedback({
        type: 'success',
        text: `[Demo Mode] Data Smartsheet periode ${formattedPeriode1} - ${formattedPeriode2} telah berhasil disinkronkan!`
      });
      setTimeout(() => {
        fetchPursData();
      }, 1000);
    } finally {
      setIsSyncing(false);
    }
  };

  // Fetch data saat ganti tab / periode
  const handleRefreshCurrentTab = () => {
    if (activeTab === 'purs') {
      fetchPursData();
    } else {
      fetchPpursData();
    }
  };

  // Clock Effect & Initial Fetch
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    fetchPursData();
    fetchPpursData();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter Data PURS
  const filteredPursData = pursList.filter(item => {
    const q = searchQuery.toLowerCase();
    const matchQuery =
      (item.no_purs || '').toLowerCase().includes(q) ||
      (item.no_ppurs || '').toLowerCase().includes(q) ||
      (item.keterangan || '').toLowerCase().includes(q) ||
      (item.nm_pemohon || '').toLowerCase().includes(q) ||
      (item.nm_divisi || '').toLowerCase().includes(q) ||
      (item.nm_unit || '').toLowerCase().includes(q) ||
      (item.no_anggaran || '').toLowerCase().includes(q);

    let matchUrgensi = true;
    if (filterUrgensi) {
      matchUrgensi = (item.urgensi || '').toLowerCase() === filterUrgensi.toLowerCase();
    }

    return matchQuery && matchUrgensi;
  });

  // Filter Data PPURS
  const filteredPpursData = ppursList.filter(item => {
    const q = searchQuery.toLowerCase();
    return (
      (item.no_ppurs || '').toLowerCase().includes(q) ||
      (item.no_purs || '').toLowerCase().includes(q) ||
      (item.kelompok || '').toLowerCase().includes(q) ||
      (item.nm_pemohon || '').toLowerCase().includes(q) ||
      (item.no_anggaran || '').toLowerCase().includes(q)
    );
  });

  const activeListData = activeTab === 'purs' ? filteredPursData : filteredPpursData;

  // Total Nilai Pengajuan
  const totalPengajuanSum = activeListData.reduce((acc, curr) => acc + (Number(curr.nilaipengajuan) || 0), 0);
  const totalDokumenSum   = ppursList.reduce((acc, curr) => acc + (Number(curr.dokumen) || 0), 0);

  // Pagination Logic
  const indexOfLast  = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = activeListData.slice(indexOfFirst, indexOfLast);
  const totalPages   = Math.ceil(activeListData.length / itemsPerPage);

  // Handler Detail Modal
  const handleOpenDetailModal = (item) => {
    if (activeTab === 'purs') {
      setSelectedPurs(item);
      setSelectedPpurs(null);
    } else {
      setSelectedPpurs(item);
      setSelectedPurs(null);
    }
    setShowDetailModal(true);
  };

  return (
    <div style={styles.appContainer}>
      {/* ══ NAVBAR COMPACT HEADER ══ */}
      <nav style={styles.navbarCustom} className="d-flex align-items-center justify-content-between text-white shadow-sm">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-6 tracking-wide d-flex align-items-center gap-2">
            <i className="bi bi-wallet2 text-warning fs-5"></i>
            SIMRS — Pengeluaran & Pengembalian Uang RS (PURS / PPURS)
          </span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="text-light" style={{ fontSize: '0.72rem', opacity: 0.9 }}>
            <i className="bi bi-calendar-event me-1"></i>{time.date} |
            <i className="bi bi-clock me-1 ms-1"></i>{time.clock}
          </span>
          <Button
            variant="warning"
            size="sm"
            className="fw-semibold text-dark shadow-sm"
            style={{ padding: '0.2rem 0.75rem', fontSize: '0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill me-1"></i> Kembali ke Home
          </Button>
        </div>
      </nav>

      {/* ══ MAIN CONTENT ══ */}
      <div style={styles.mainContent}>

        {/* ── Stats Summary Bar ── */}
        <Row className="g-2 mb-2">
          <Col md={3} sm={6}>
            <div style={{ background: '#f0fdfa', border: '1.5px solid #0d948822', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#0d9488', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-receipt-cutoff text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#0d9488', lineHeight: 1.1 }}>
                  {activeTab === 'purs' ? pursList.length : ppursList.length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>
                  Total Record {activeTab === 'purs' ? 'PURS' : 'PPURS'}
                </div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#f0fdf4', border: '1.5px solid #16a34a22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#16a34a', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-cash-coin text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#16a34a', lineHeight: 1.1 }}>
                  {formatRp(totalPengajuanSum)}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Total Nilai Pengajuan</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#f5f3ff', border: '1.5px solid #7c3aed22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#7c3aed', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-file-earmark-check-fill text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#7c3aed', lineHeight: 1.1 }}>
                  {formatRp(totalDokumenSum)}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Total Nilai Dokumen PPURS</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#eff6ff', border: '1.5px solid #2563eb22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#2563eb', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-safe-fill text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '0.92rem', color: '#2563eb', lineHeight: 1.1 }}>
                  {formatRp(pursList.length > 0 ? pursList[0].saldoanggaran || 0 : 0)}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Sisa Saldo Anggaran</div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Tab Switcher Bar ── */}
        <div className="d-flex align-items-center gap-2 mb-2 bg-white p-2 rounded-3 border shadow-sm">
          <Button
            variant={activeTab === 'purs' ? 'teal' : 'light'}
            className={`fw-bold d-flex align-items-center gap-1.5 ${activeTab === 'purs' ? 'text-white' : 'text-secondary'}`}
            style={{
              fontSize: '0.78rem',
              borderRadius: '8px',
              padding: '0.38rem 1.1rem',
              background: activeTab === 'purs' ? 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' : '#f8fafc',
              borderColor: activeTab === 'purs' ? '#0d9488' : '#cbd5e1'
            }}
            onClick={() => { setActiveTab('purs'); setCurrentPage(1); }}
          >
            <i className="bi bi-wallet2 fs-6"></i>
            <span>1. Pengeluaran Uang RS (PURS)</span>
            <Badge bg={activeTab === 'purs' ? 'light' : 'secondary'} text={activeTab === 'purs' ? 'dark' : 'white'} className="ms-1 font-monospace">
              {pursList.length}
            </Badge>
          </Button>

          <Button
            variant={activeTab === 'ppurs' ? 'teal' : 'light'}
            className={`fw-bold d-flex align-items-center gap-1.5 ${activeTab === 'ppurs' ? 'text-white' : 'text-secondary'}`}
            style={{
              fontSize: '0.78rem',
              borderRadius: '8px',
              padding: '0.38rem 1.1rem',
              background: activeTab === 'ppurs' ? 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' : '#f8fafc',
              borderColor: activeTab === 'ppurs' ? '#0d9488' : '#cbd5e1'
            }}
            onClick={() => { setActiveTab('ppurs'); setCurrentPage(1); fetchPpursData(); }}
          >
            <i className="bi bi-arrow-counterclockwise fs-6"></i>
            <span>2. Pengembalian Peminjaman Uang (PPURS)</span>
            <Badge bg={activeTab === 'ppurs' ? 'light' : 'secondary'} text={activeTab === 'ppurs' ? 'dark' : 'white'} className="ms-1 font-monospace">
              {ppursList.length}
            </Badge>
          </Button>
        </div>

        {/* ── Main Card ── */}
        <Card style={styles.mainCard} className="shadow-sm border-0">
          {/* Card Header */}
          <div style={{ ...styles.cardHeader, ...styles.bgMenuHeader }}
            className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-bold">
              <i className="bi bi-currency-dollar me-1 text-warning"></i>
              {activeTab === 'purs' ? 'Daftar Pengeluaran Uang Rumah Sakit (PURS)' : 'Daftar Pengembalian Peminjaman Uang Perusahaan (PPURS)'}
            </span>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {activeTab === 'purs' && (
                <Button
                  variant="primary"
                  size="sm"
                  className="fw-bold d-flex align-items-center gap-1.5 shadow-sm text-white"
                  style={{ fontSize: '0.73rem', background: '#2563eb', borderColor: '#2563eb', borderRadius: '6px' }}
                  onClick={handleOpenSyncModal}
                >
                  <i className="bi bi-cloud-arrow-down-fill fs-6"></i>
                  <span>Sinkron Data Smartsheet</span>
                </Button>
              )}
              <Button
                variant="outline-light"
                size="sm"
                onClick={handleRefreshCurrentTab}
                disabled={isLoading}
                style={{ fontSize: '0.73rem', borderRadius: '6px' }}
              >
                {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-clockwise"></i>} Refresh Data
              </Button>
            </div>
          </div>

          <Card.Body style={{ padding: '0.6rem' }}>
            {/* ── Filter Controls (Periode Tanggal & Pencarian) ── */}
            <Row className="g-2 mb-2 align-items-center">
              {/* Periode Tgl Awal */}
              <Col md={3} sm={6}>
                <Form.Group className="d-flex align-items-center gap-1">
                  <Form.Label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                    Awal:
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={periode}
                    onChange={e => setPeriode(e.target.value)}
                    style={{ fontSize: '0.78rem' }}
                  />
                </Form.Group>
              </Col>

              {/* Periode Tgl Akhir */}
              <Col md={3} sm={6}>
                <Form.Group className="d-flex align-items-center gap-1">
                  <Form.Label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                    Akhir:
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={periode2}
                    onChange={e => setPeriode2(e.target.value)}
                    style={{ fontSize: '0.78rem' }}
                  />
                </Form.Group>
              </Col>

              {/* Tombol Filter Periode */}
              <Col md={2} sm={6}>
                <Button
                  variant="teal"
                  size="sm"
                  className="w-100 fw-bold d-flex align-items-center justify-content-center gap-1 text-white shadow-sm"
                  style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', borderColor: '#0d9488', fontSize: '0.75rem' }}
                  onClick={handleRefreshCurrentTab}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-funnel-fill"></i>}
                  <span>Filter Periode</span>
                </Button>
              </Col>

              {/* Filter Urgensi (Khusus PURS) */}
              {activeTab === 'purs' && (
                <Col md={2} sm={6}>
                  <Form.Select
                    size="sm"
                    value={filterUrgensi}
                    onChange={e => { setFilterUrgensi(e.target.value); setCurrentPage(1); }}
                    style={{ fontSize: '0.78rem' }}
                  >
                    <option value="">Semua Urgensi</option>
                    <option value="Cito">Cito</option>
                    <option value="Tidak Cito">Tidak Cito</option>
                  </Form.Select>
                </Col>
              )}

              {/* Rows Per Page */}
              <Col md={activeTab === 'purs' ? 2 : 4} sm={6}>
                <Form.Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ fontSize: '0.78rem' }}
                >
                  <option value={10}>10 Baris per Halaman</option>
                  <option value={20}>20 Baris per Halaman</option>
                  <option value={50}>50 Baris per Halaman</option>
                  <option value={100}>100 Baris per Halaman</option>
                </Form.Select>
              </Col>

              {/* Search Bar */}
              <Col md={12}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ background: '#f8fafc', fontSize: '0.78rem' }}>
                    <i className="bi bi-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={activeTab === 'purs' ? "Cari No PURS, PPURS, Keterangan, Pemohon, Divisi, Unit, No Anggaran..." : "Cari No PPURS, PURS, Kelompok Belanja, Pemohon, No Anggaran..."}
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{ fontSize: '0.78rem' }}
                  />
                  {searchQuery && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setSearchQuery('')}>
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {fetchError && (
              <div className="alert alert-warning py-1 px-3 mb-2" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>{fetchError}
              </div>
            )}

            {/* ── Data Table PURS / PPURS ── */}
            <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 330px)', overflowY: 'auto' }}>
              {activeTab === 'purs' ? (
                /* ══ TAB 1: TABEL PURS ══ */
                <Table responsive hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                  <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="text-center" style={{ width: '35px' }}>#</th>
                      <th className="text-center" style={{ width: '130px' }}>No. PURS / PPURS</th>
                      <th className="text-center" style={{ width: '85px' }}>Tanggal</th>
                      <th>Divisi & Unit Pemohon</th>
                      <th>Keterangan Pengajuan</th>
                      <th>Pemohon & NIK</th>
                      <th className="text-end" style={{ color: '#4ade80' }}>Nilai Pengajuan</th>
                      <th className="text-end">Saldo Anggaran</th>
                      <th className="text-center" style={{ width: '85px' }}>Urgensi</th>
                      <th className="text-center" style={{ width: '70px' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <Spinner animation="border" variant="teal" size="sm" className="me-2" />
                          <span>Memuat data pengeluaran uang rumah sakit (PURS)...</span>
                        </td>
                      </tr>
                    ) : currentItems.length > 0 ? (
                      currentItems.map((item, idx) => (
                        <tr key={idx}>
                          <td className="text-center text-muted">{indexOfFirst + idx + 1}</td>
                          <td>
                            <div className="fw-bold text-dark font-monospace" style={{ fontSize: '0.73rem' }}>{item.no_purs}</div>
                            <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                              <Badge bg="secondary" className="font-monospace" style={{ fontSize: '0.63rem' }}>
                                {item.no_ppurs}
                              </Badge>
                            </div>
                          </td>
                          <td className="text-center fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                            {item.tanggal}
                          </td>
                          <td>
                            <div className="fw-bold text-primary" style={{ fontSize: '0.74rem' }}>{item.nm_divisi}</div>
                            <div className="text-muted" style={{ fontSize: '0.68rem' }}><i className="bi bi-geo-alt me-1"></i>{item.nm_unit}</div>
                          </td>
                          <td className="fw-bold text-dark" style={{ fontSize: '0.75rem' }}>
                            {item.keterangan}
                          </td>
                          <td>
                            <div className="fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>{item.nm_pemohon}</div>
                            <div className="text-muted" style={{ fontSize: '0.67rem' }}>NIK: <span className="font-monospace">{item.nik_pemohon}</span></div>
                          </td>
                          <td className="text-end fw-bold text-success" style={{ fontSize: '0.78rem' }}>
                            {formatRp(item.nilaipengajuan)}
                          </td>
                          <td className="text-end text-muted" style={{ fontSize: '0.73rem' }}>
                            {formatRp(item.saldoanggaran)}
                          </td>
                          <td className="text-center">
                            <Badge bg={item.urgensi === 'Cito' ? 'danger' : 'success'} style={{ fontSize: '0.68rem' }}>
                              {item.urgensi}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Detail Rincian PURS</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-info"
                                style={{ padding: '0.15rem 0.45rem', fontSize: '0.7rem', borderRadius: '5px' }}
                                onClick={() => handleOpenDetailModal(item)}
                              >
                                <i className="bi bi-eye-fill"></i>
                              </Button>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4 text-muted">
                          Tidak ada data PURS yang ditemukan.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              ) : (
                /* ══ TAB 2: TABEL PPURS (show_ppurs) ══ */
                <Table responsive hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                  <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="text-center" style={{ width: '35px' }}>#</th>
                      <th className="text-center" style={{ width: '135px' }}>No. PPURS / PURS</th>
                      <th className="text-center" style={{ width: '85px' }}>Tanggal</th>
                      <th>Nama Pemohon</th>
                      <th>Kelompok Belanja</th>
                      <th className="text-center">No. Anggaran</th>
                      <th className="text-end" style={{ color: '#4ade80' }}>Nilai Pengajuan</th>
                      <th className="text-end" style={{ color: '#c084fc' }}>Dokumen</th>
                      <th className="text-end">Tunai / Bank</th>
                      <th className="text-end" style={{ color: '#60a5fa' }}>Sisa PURS</th>
                      <th className="text-center" style={{ width: '70px' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="11" className="text-center py-4">
                          <Spinner animation="border" variant="teal" size="sm" className="me-2" />
                          <span>Memuat data pengembalian peminjaman uang perusahaan (PPURS)...</span>
                        </td>
                      </tr>
                    ) : currentItems.length > 0 ? (
                      currentItems.map((item, idx) => (
                        <tr key={idx}>
                          <td className="text-center text-muted">{indexOfFirst + idx + 1}</td>
                          <td>
                            <Badge bg="teal" style={{ background: '#0d9488', fontSize: '0.73rem' }} className="font-monospace mb-1">
                              {item.no_ppurs}
                            </Badge>
                            <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                              Ref: <span className="font-monospace text-dark">{item.no_purs}</span>
                            </div>
                          </td>
                          <td className="text-center fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                            {item.tanggal}
                          </td>
                          <td className="fw-bold text-dark" style={{ fontSize: '0.75rem' }}>
                            {item.nm_pemohon}
                          </td>
                          <td>
                            <Badge bg="info" text="dark" style={{ fontSize: '0.68rem' }}>
                              {item.kelompok}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="dark" className="font-monospace" style={{ fontSize: '0.7rem', backgroundColor: '#1e293b' }}>
                              {item.no_anggaran}
                            </Badge>
                          </td>
                          <td className="text-end fw-bold text-success" style={{ fontSize: '0.78rem' }}>
                            {formatRp(item.nilaipengajuan)}
                          </td>
                          <td className="text-end fw-semibold text-purple" style={{ fontSize: '0.75rem', color: '#6b21a8' }}>
                            {formatRp(item.dokumen)}
                          </td>
                          <td className="text-end text-muted" style={{ fontSize: '0.72rem' }}>
                            <div>Tunai: {formatRp(item.tunai)}</div>
                            <div>Bank: {formatRp(item.bank)}</div>
                          </td>
                          <td className="text-end fw-bold text-primary" style={{ fontSize: '0.78rem' }}>
                            {formatRp(item.sisa_purs)}
                          </td>
                          <td className="text-center">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Detail Rincian PPURS</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-info"
                                style={{ padding: '0.15rem 0.45rem', fontSize: '0.7rem', borderRadius: '5px' }}
                                onClick={() => handleOpenDetailModal(item)}
                              >
                                <i className="bi bi-eye-fill"></i>
                              </Button>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center py-4 text-muted">
                          Tidak ada data pengembalian PPURS yang ditemukan.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                  {[...Array(Math.min(totalPages, 10))].map((_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  {totalPages > 10 && <Pagination.Ellipsis disabled />}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}

            {/* ── Footer Info ── */}
            <div className="d-flex justify-content-between mt-3 pt-2 border-top text-muted" style={{ fontSize: '0.72rem' }}>
              <span>
                Menampilkan{' '}
                <strong>{activeListData.length > 0 ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, activeListData.length)}</strong>{' '}
                dari <strong>{activeListData.length}</strong> data ({activeTab.toUpperCase()})
              </span>
              <span>IP Server: <strong>{getApiUrl()}</strong></span>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* ══ MODAL DETAIL PURS / PPURS ══ */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0d9488 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-wallet2 text-warning fs-5"></i>
            <span>{selectedPurs ? 'Detail Pengeluaran Uang RS (PURS)' : 'Detail Pengembalian Peminjaman Uang (PPURS)'}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {selectedPurs ? (
            /* Detail PURS */
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="p-3" style={{ fontSize: '0.78rem' }}>
                <Row className="g-2">
                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nomor PURS</div>
                    <Badge bg="teal" style={{ background: '#0d9488', fontSize: '0.8rem' }} className="font-monospace">{selectedPurs.no_purs}</Badge>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>No. PPURS: <strong className="font-monospace text-dark">{selectedPurs.no_ppurs}</strong></div>
                  </Col>
                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Tanggal Pengajuan</div>
                    <div className="fw-bold text-dark fs-6">{selectedPurs.tanggal}</div>
                    <div>
                      <Badge bg={selectedPurs.urgensi === 'Cito' ? 'danger' : 'success'} style={{ fontSize: '0.68rem' }}>
                        Urgensi: {selectedPurs.urgensi}
                      </Badge>
                    </div>
                  </Col>

                  <Col md={12} className="bg-white p-2 rounded border mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Keterangan / Keperluan Pengajuan</div>
                    <div className="fw-bold text-dark fs-6">{selectedPurs.keterangan}</div>
                  </Col>

                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Divisi Pemohon</div>
                    <div className="fw-semibold text-primary">{selectedPurs.nm_divisi} (Kode: {selectedPurs.kd_divisi})</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Unit Pemohon</div>
                    <div className="fw-semibold text-dark">{selectedPurs.nm_unit} (Kode: {selectedPurs.kd_unit})</div>
                  </Col>

                  <Col md={6} className="mt-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pemohon Pengajuan</div>
                    <div className="fw-bold text-dark">{selectedPurs.nm_pemohon}</div>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>NIK Pemohon: <span className="font-monospace">{selectedPurs.nik_pemohon}</span></div>
                  </Col>
                  <Col md={6} className="mt-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Kepala Bagian (Kabag)</div>
                    <div className="fw-bold text-dark">{selectedPurs.nm_kabag}</div>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>NIK Kabag: <span className="font-monospace">{selectedPurs.nik_kabag}</span></div>
                  </Col>

                  <Col md={4} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>No. Anggaran</div>
                    <Badge bg="dark" className="font-monospace">{selectedPurs.no_anggaran}</Badge>
                  </Col>
                  <Col md={4} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nilai Pengajuan</div>
                    <div className="fw-bold text-success fs-6">{formatRp(selectedPurs.nilaipengajuan)}</div>
                  </Col>
                  <Col md={4} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Saldo Anggaran</div>
                    <div className="fw-semibold text-dark fs-6">{formatRp(selectedPurs.saldoanggaran)}</div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ) : selectedPpurs ? (
            /* Detail PPURS */
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="p-3" style={{ fontSize: '0.78rem' }}>
                <Row className="g-2">
                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nomor PPURS</div>
                    <Badge bg="teal" style={{ background: '#0d9488', fontSize: '0.8rem' }} className="font-monospace">{selectedPpurs.no_ppurs}</Badge>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Ref PURS: <strong className="font-monospace text-dark">{selectedPpurs.no_purs}</strong></div>
                  </Col>
                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Tanggal Pengembalian</div>
                    <div className="fw-bold text-dark fs-6">{selectedPpurs.tanggal}</div>
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>Pemohon: <strong>{selectedPpurs.nm_pemohon}</strong></div>
                  </Col>

                  <Col md={12} className="bg-white p-2 rounded border mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Kelompok Belanja</div>
                    <Badge bg="info" text="dark" className="fs-6">{selectedPpurs.kelompok}</Badge>
                  </Col>

                  <Col md={4}>
                    <div className="p-2 rounded bg-light border">
                      <div className="text-muted" style={{ fontSize: '0.65rem' }}>Nilai Pengajuan</div>
                      <div className="fw-bold text-success" style={{ fontSize: '0.85rem' }}>{formatRp(selectedPpurs.nilaipengajuan)}</div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="p-2 rounded bg-purple-subtle border border-purple-subtle" style={{ background: '#f3e8ff', borderColor: '#d8b4fe' }}>
                      <div className="text-muted" style={{ fontSize: '0.65rem' }}>Nilai Dokumen</div>
                      <div className="fw-bold" style={{ fontSize: '0.85rem', color: '#6b21a8' }}>{formatRp(selectedPpurs.dokumen)}</div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="p-2 rounded bg-primary-subtle border border-primary-subtle" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
                      <div className="text-muted" style={{ fontSize: '0.65rem' }}>Sisa PURS</div>
                      <div className="fw-bold text-primary" style={{ fontSize: '0.85rem' }}>{formatRp(selectedPpurs.sisa_purs)}</div>
                    </div>
                  </Col>

                  <Col md={6} className="mt-2 border-top pt-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pengembalian Tunai</div>
                    <div className="fw-semibold text-dark">{formatRp(selectedPpurs.tunai)}</div>
                  </Col>

                  <Col md={6} className="mt-2 border-top pt-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pengembalian Bank</div>
                    <div className="fw-semibold text-dark">{formatRp(selectedPpurs.bank)}</div>
                  </Col>

                  <Col md={12} className="mt-2 border-top pt-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>No. Anggaran</div>
                    <Badge bg="dark" className="font-monospace">{selectedPpurs.no_anggaran}</Badge>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ) : null}

          <div className="d-flex justify-content-end gap-2 border-top pt-2">
            <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
              Tutup
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI & PROGRESS SINKRON DATA SMARTSHEET (getdatasmartsheet) ══ */}
      <Modal show={showSyncModal} onHide={handleCloseSyncModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-cloud-arrow-down-fill fs-5 text-warning"></i>
            <span>Sinkronisasi Data Smartsheet PURS</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {!isSyncing && !syncFeedback ? (
            /* Langkah 1: Pertanyaan Konfirmasi */
            <div>
              <div className="alert alert-primary border-0 shadow-sm p-3 mb-3" style={{ background: '#eff6ff', borderLeft: '4px solid #3b82f6', borderRadius: '10px' }}>
                <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-question-circle-fill me-1 text-primary"></i> Konfirmasi Sinkronisasi Data
                </div>
                <div className="text-dark" style={{ fontSize: '0.78rem' }}>
                  Apakah Anda yakin ingin melakukan <strong>Sinkronisasi Data PURS dari Smartsheet</strong> untuk periode <strong>{formatYmdToDdmmyyyy(periode)} s/d {formatYmdToDdmmyyyy(periode2)}</strong> ke sistem SIMRS?
                </div>
              </div>

              <p className="text-muted text-center mb-3" style={{ fontSize: '0.73rem' }}>
                Sistem akan memanggil backend <code>/getdatasmartsheet</code> dan memperbarui riwayat transaksi pengeluaran.
              </p>

              <div className="d-flex justify-content-center gap-2 border-top pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ borderRadius: '6px', padding: '0.35rem 1.2rem', fontSize: '0.78rem' }}
                  onClick={handleCloseSyncModal}
                >
                  Tidak / Kembali
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="fw-bold text-white shadow-sm d-flex align-items-center gap-1"
                  style={{ borderRadius: '6px', padding: '0.35rem 1.2rem', fontSize: '0.78rem', background: '#2563eb', borderColor: '#2563eb' }}
                  onClick={handleProcessSyncSmartsheet}
                >
                  <i className="bi bi-check-circle-fill me-1"></i>
                  <span>Ya, Lakukan Sinkronisasi</span>
                </Button>
              </div>
            </div>
          ) : (
            /* Langkah 2: Progress Bar & Result */
            <div>
              <div className="text-center py-2 mb-2">
                <i className={`bi ${syncProgress === 100 ? 'bi-check-circle-fill text-success' : 'bi-cloud-download-fill text-primary'} display-5`}></i>
                <div className="fw-bold text-dark mt-2" style={{ fontSize: '0.88rem' }}>
                  {syncStatusText || 'Sedang memproses sinkronisasi data...'}
                </div>
              </div>

              <div className="mb-3 px-2">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span>Progres Sinkronisasi</span>
                  <span className="fw-bold text-primary">{syncProgress}%</span>
                </div>
                <ProgressBar
                  animated={syncProgress < 100}
                  striped
                  variant={syncProgress === 100 ? 'success' : 'primary'}
                  now={syncProgress}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>

              {syncFeedback && (
                <div className={`alert alert-${syncFeedback.type} py-2 px-3 mb-3`} style={{ fontSize: '0.76rem' }}>
                  <i className="bi bi-info-circle-fill me-1"></i>
                  {syncFeedback.text}
                </div>
              )}

              {syncProgress === 100 && (
                <div className="d-flex justify-content-end border-top pt-2">
                  <Button variant="primary" size="sm" className="fw-bold px-3" onClick={handleCloseSyncModal}>
                    Selesai
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

// ══════════════ STYLES ══════════════
const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex', flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.25rem 1rem', flexShrink: 0,
  },
  mainContent: {
    flex: 1, padding: '0.4rem', overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '12px', border: '1.5px solid #cbd5e1',
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
    background: '#ffffff', overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.4rem 0.75rem', color: '#ffffff', fontWeight: 600, fontSize: '0.83rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
};

export default DataPURS;
